import * as Sentry from '@sentry/react';
import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import ActionButton from 'components/Widget/ActionButton';
import * as api from 'dragonmouse-shared/api/forge';
import { ChatMode, ChatStatus } from 'dragonmouse-shared/interfaces';
import { useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectActiveScene, selectActiveSceneChat, selectActiveStyle } from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';

interface Props {
	label: string;
	prompt: string;
}

const FollowUpAction = ({ label, prompt }: Props) => {
	const [isLoading, setIsLoading] = useState(false);
	const { chat, scene, activeStyle } = useSelector(
		(r: RootReducer) => ({
			chat: selectActiveSceneChat(r.db),
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			activeStyle: selectActiveStyle(r.db, r.mode.activeSceneId),
		}),
		shallowEqual
	);

	const lastClickRef = useRef<number>(0);

	const onClick = async () => {
		if (Date.now() - lastClickRef.current < 500) return;
		lastClickRef.current = Date.now();

		if (isLoading || !chat) return;

		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		setIsLoading(true);
		try {
			await forge.updateSceneChat({
				update: {
					id: chat.id,
					metadata: {
						status: ChatStatus.PROCESSING,
						statusMessage: '',
					},
				},
			});

			const entry = {
				kind: 'user' as const,
				message: prompt,
				sourceFrameIds: [],
				attachments: [],
			};
			const req: api.AddUserEntrySceneChatRequest = {
				chatId: chat.id,
				entry,
				chatMode: ChatMode.DESIGN_AND_CHAT,
				style: activeStyle,
			};
			await forge.addSceneChatUserEntry.withToast()(req);
		} catch (e) {
			Sentry.captureException(e);
			toast.error('Failed to send follow-up');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<ActionButton
			icon={IconType.GENERATE}
			label={label}
			loadingLabel="Sending…"
			isLoading={isLoading}
			prompt={prompt}
			onClick={onClick}
		/>
	);
};

export default FollowUpAction;
